import React from 'react';

/**
 * ErrorBoundary — catches render-time errors in its subtree and shows a
 * recovery UI instead of a white/black screen.
 *
 * Usage:
 *   <ErrorBoundary fallbackTitle="Erreur lors de la demande de rendez-vous">
 *     <Screen_MemberAppointmentRequest />
 *   </ErrorBoundary>
 *
 * If no fallbackTitle prop, uses a generic message.
 */
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, info) {
    // Surface to the console so we can see the real error in prod
    // eslint-disable-next-line no-console
    console.error('[ErrorBoundary] caught render error:', error, info);
  }

  handleBack = () => {
    // Prefer history.back so the user returns to the previous screen.
    // If there's no history, fall back to home.
    if (typeof window !== 'undefined' && window.history.length > 1) {
      window.history.back();
    } else {
      window.location.assign('/');
    }
  };

  handleReload = () => {
    if (typeof window !== 'undefined') window.location.reload();
  };

  render() {
    if (!this.state.hasError) return this.props.children;

    const isDark =
      typeof document !== 'undefined' &&
      document.documentElement.classList.contains('dark');

    return (
      <div
        style={{
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '24px',
          background: isDark ? '#272727' : '#F2F1EB',
          color: isDark ? '#F2F1EB' : '#272727',
          textAlign: 'center',
        }}
        data-testid="error-boundary-fallback"
      >
        <h2 style={{ fontSize: 18, fontWeight: 700, margin: 0, marginBottom: 8 }}>
          {this.props.fallbackTitle || 'Une erreur est survenue'}
        </h2>
        <p
          style={{
            fontSize: 14,
            margin: 0,
            marginBottom: 24,
            opacity: 0.7,
            maxWidth: 320,
          }}
        >
          {this.props.fallbackMessage ||
            "Désolé, un problème inattendu s'est produit. Vous pouvez revenir en arrière ou recharger la page."}
        </p>
        <div style={{ display: 'flex', gap: 12 }}>
          <button
            onClick={this.handleBack}
            style={{
              padding: '10px 20px',
              borderRadius: 12,
              border: 'none',
              background: '#9398DE',
              color: 'white',
              fontWeight: 600,
              fontSize: 14,
              cursor: 'pointer',
            }}
          >
            ← Retour
          </button>
          <button
            onClick={this.handleReload}
            style={{
              padding: '10px 20px',
              borderRadius: 12,
              border: isDark ? '1px solid #444' : '1px solid #E5E7EB',
              background: 'transparent',
              color: isDark ? '#F2F1EB' : '#272727',
              fontWeight: 600,
              fontSize: 14,
              cursor: 'pointer',
            }}
          >
            Recharger
          </button>
        </div>
        {/* Dev-only details */}
        {process.env.NODE_ENV !== 'production' && this.state.error && (
          <details style={{ marginTop: 24, fontSize: 12, opacity: 0.6, maxWidth: 400 }}>
            <summary style={{ cursor: 'pointer' }}>Détails techniques</summary>
            <pre
              style={{
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
                marginTop: 8,
              }}
            >
              {String(this.state.error?.message || this.state.error)}
            </pre>
          </details>
        )}
      </div>
    );
  }
}

export default ErrorBoundary;
